import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore } from '@firebase/firestore';
import { collection, doc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { app } from '../firebase.js';
import defaulProfPic from '../Images/defprofile.png';
import MaskedPhone from '../Components/MaskedPhone';
import { logAdminActivity } from '../utils/activityLogger';

const normalizeDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  const dateValue = new Date(value);
  return Number.isNaN(dateValue.getTime()) ? null : dateValue;
};

const formatDateTime = (value) => {
  const dateValue = normalizeDate(value);
  if (!dateValue) return '—';
  const datePart = dateValue.toLocaleDateString();
  const timePart = dateValue.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${datePart} • ${timePart}`;
};

const formatDateOnly = (value) => {
  const dateValue = normalizeDate(value);
  if (!dateValue) return '—';
  return dateValue.toLocaleDateString();
};

const getResumeTone = (resumeDate) => {
  if (!resumeDate) return 'neutral';
  const dateValue = normalizeDate(resumeDate);
  if (!dateValue) return 'neutral';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dateValue.setHours(0, 0, 0, 0);

  const daysAway = Math.ceil((dateValue.getTime() - today.getTime()) / 86400000);
  if (daysAway < 0) return 'overdue';
  if (daysAway <= 7) return 'soon';
  return 'scheduled';
};

function LearningBreaksComp() {
  const db = getFirestore(app);
  const auth = getAuth();
  const navigate = useNavigate();
  const lastSearchRef = useRef('');
  const lastCourseRef = useRef('All');

  const [pausedStudents, setPausedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resumingId, setResumingId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourse, setFilterCourse] = useState('All');

  const loadPausedStudents = async () => {
    setLoading(true);
    try {
      const pausedQuery = query(collection(db, 'users'), where('userStatus', '==', 'Paused'));
      const snapshot = await getDocs(pausedQuery);
      const students = snapshot.docs.map((studentDoc) => ({
        id: studentDoc.id,
        ...studentDoc.data()
      }));

      students.sort((first, second) => {
        const firstDate = normalizeDate(first.pausedAt || first.learningPause?.pausedAt || first.appDate)?.getTime() || 0;
        const secondDate = normalizeDate(second.pausedAt || second.learningPause?.pausedAt || second.appDate)?.getTime() || 0;
        return secondDate - firstDate;
      });

      setPausedStudents(students);
    } catch (error) {
      console.error('Failed to load learning breaks:', error);
      swal('Error', 'Unable to load students on learning break.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPausedStudents();
  }, []);

  useEffect(() => {
    const trimmed = searchTerm.trim();
    const timeoutId = setTimeout(() => {
      if (trimmed === lastSearchRef.current) return;
      lastSearchRef.current = trimmed;

      if (trimmed) {
        logAdminActivity({
          action: 'Search',
          details: `Learning Breaks search: "${trimmed}"`,
          meta: { page: 'Learning Breaks', query: trimmed }
        });
      }
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (filterCourse === lastCourseRef.current) return;
    lastCourseRef.current = filterCourse;
    logAdminActivity({
      action: 'Filter',
      details: `Learning Breaks course filter: ${filterCourse}`,
      meta: { page: 'Learning Breaks', filter: 'course', value: filterCourse }
    });
  }, [filterCourse]);

  const uniqueCourses = useMemo(() => (
    Array.from(new Set(pausedStudents.map((student) => student.course).filter(Boolean))).sort()
  ), [pausedStudents]);

  const filteredPausedStudents = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return pausedStudents.filter((student) => {
      const matchesSearch = !search || [
        student.bio,
        student.course,
        student.nationality,
        student.phoneNumber,
        student.pauseReason,
        student.learningPause?.reason
      ].some((value) => String(value || '').toLowerCase().includes(search));

      const matchesCourse = filterCourse === 'All' || student.course === filterCourse;
      return matchesSearch && matchesCourse;
    });
  }, [filterCourse, pausedStudents, searchTerm]);

  const resumeStudent = async (student) => {
    const confirmed = await swal({
      title: 'Resume learning?',
      text: `${student.bio || 'This student'} will move back to Active Students.`,
      icon: 'warning',
      buttons: ['Cancel', 'Resume'],
      dangerMode: false
    });

    if (!confirmed) return;

    setResumingId(student.id);
    try {
      const adminUser = auth.currentUser;
      await updateDoc(doc(db, 'users', student.id), {
        userStatus: 'Student',
        resumedAt: serverTimestamp(),
        resumedBy: adminUser?.uid || null,
        resumedByEmail: adminUser?.email || '',
        learningPause: {
          status: 'resumed',
          previousStatus: 'Paused',
          reason: student.pauseReason || student.learningPause?.reason || '',
          notes: student.pauseNotes || student.learningPause?.notes || '',
          expectedResumeDate: student.expectedResumeDate || student.learningPause?.expectedResumeDate || '',
          pausedAt: student.pausedAt || student.learningPause?.pausedAt || null,
          resumedAt: serverTimestamp(),
          resumedBy: adminUser?.uid || null,
          resumedByEmail: adminUser?.email || ''
        }
      });

      setPausedStudents((previous) => previous.filter((item) => item.id !== student.id));
      logAdminActivity({
        action: 'Resume Learning',
        details: `Resumed learning for ${student.bio || 'student'}`,
        meta: {
          page: 'Learning Breaks',
          studentId: student.userId || student.id,
          studentName: student.bio || ''
        }
      });
      swal('Updated', `${student.bio || 'Student'} is now active again.`, 'success');
    } catch (error) {
      console.error('Failed to resume student learning:', error);
      swal('Error', error.message || 'Unable to resume this student.', 'error');
    } finally {
      setResumingId('');
    }
  };

  const viewStudent = (student) => {
    logAdminActivity({
      action: 'View Student',
      details: `Opened student profile: ${student.bio || 'student'}`,
      meta: { page: 'Learning Breaks', studentId: student.userId || student.id, studentName: student.bio || '' }
    });
    navigate('/view-student', { state: { myData: student.userId || student.id } });
  };

  const expectedThisMonth = pausedStudents.filter((student) => {
    const resumeDate = normalizeDate(student.expectedResumeDate || student.learningPause?.expectedResumeDate);
    if (!resumeDate) return false;
    const today = new Date();
    return resumeDate.getMonth() === today.getMonth() && resumeDate.getFullYear() === today.getFullYear();
  }).length;

  const overdueCount = pausedStudents.filter((student) => (
    getResumeTone(student.expectedResumeDate || student.learningPause?.expectedResumeDate) === 'overdue'
  )).length;

  return (
    <div className="clientsComp applicationsPage learningBreaksPage">
      <div className="appsHeader">
        <div>
          <p className="appsKicker">Students</p>
          <h4>Learning Breaks</h4>
          <p className="appsSubtext">Students temporarily paused from learning. Pause a student from their profile page.</p>
        </div>
        <div className="appsHeaderStats">
          <div className="appsStatChip">
            <span>On Break</span>
            <strong>{pausedStudents.length}</strong>
          </div>
          <div className="appsStatChip">
            <span>This Month</span>
            <strong>{expectedThisMonth}</strong>
          </div>
          <div className="appsStatChip">
            <span>Overdue</span>
            <strong>{overdueCount}</strong>
          </div>
        </div>
      </div>

      <div className="appsFiltersCard learningBreaksFilters">
        <div className="appsSearch input-group">
          <span className="input-group-text"><FontAwesomeIcon icon={faSearch} /></span>
          <Form.Control
            type="text"
            placeholder="Search name, course, phone, reason..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <div className="appsFilterGroup">
          <Form.Select className="filterSelect" value={filterCourse} onChange={(event) => setFilterCourse(event.target.value)}>
            <option value="All">All Courses</option>
            {uniqueCourses.map((course) => (
              <option key={course} value={course}>{course}</option>
            ))}
          </Form.Select>
        </div>
      </div>

      <div className="appsTableCard">
        <div className="appsTableHeader">
          <h6>Students on Learning Break</h6>
          <span>{filteredPausedStudents.length} results</span>
        </div>
        <div className="appsTableWrap">
          <Table hover responsive className="appsTable">
            <thead>
              <tr>
                <th>Student</th>
                <th>Paused On</th>
                <th>Expected Resume</th>
                <th>Course</th>
                <th>Phone</th>
                <th>Reason</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="7" className="appsEmptyState">
                    Loading students on learning break...
                  </td>
                </tr>
              )}
              {!loading && filteredPausedStudents.map((student) => {
                const resumeDate = student.expectedResumeDate || student.learningPause?.expectedResumeDate;
                const resumeTone = getResumeTone(resumeDate);

                return (
                  <tr key={student.id}>
                    <td>
                      <div className="appsStudentCell">
                        <div className='appDefProfPic'>
                          <img src={student.profilePic || defaulProfPic} alt="profile" />
                        </div>
                        <div>
                          <p className="appsStudentName">{student.bio || 'Unnamed student'}</p>
                          <span className="appsStudentMeta">{student.cohort || 'Cohort not set'}</span>
                        </div>
                      </div>
                    </td>
                    <td>{formatDateTime(student.pausedAt || student.learningPause?.pausedAt)}</td>
                    <td>
                      <span className={`learningBreaksPill learningBreaksPill--${resumeTone}`}>
                        {formatDateOnly(resumeDate)}
                      </span>
                    </td>
                    <td>{student.course || '—'}</td>
                    <td>
                      <MaskedPhone
                        phoneNumber={student.phoneNumber}
                        studentId={student.userId}
                        studentName={student.bio}
                      />
                    </td>
                    <td className="learningBreaksReason">
                      {student.pauseReason || student.learningPause?.reason || 'Learning pause'}
                    </td>
                    <td>
                      <div className="learningBreaksActions">
                        <button className='appsViewBtn' onClick={() => viewStudent(student)}>
                          View
                        </button>
                        <button
                          className='appsViewBtn learningBreaksResumeBtn'
                          onClick={() => resumeStudent(student)}
                          disabled={resumingId === student.id}
                        >
                          {resumingId === student.id ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faRotateLeft} />}
                          Resume
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && filteredPausedStudents.length === 0 && (
                <tr>
                  <td colSpan="7" className="appsEmptyState">
                    No students are on a learning break yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}

export default LearningBreaksComp;
